import { useEffect } from "react";

/**
 * Lightweight SEO head manager for the SPA.
 * Sets <title>, <meta name="description">, <meta name="robots">, and canonical link
 * per route via useEffect. Runs on the client — Googlebot renders JS, so this is
 * indexed correctly. For Semrush/other non-rendering crawlers, we complement this
 * with per-route static sitemap.xml + robots.txt.
 */
const SITE_NAME = "South on Leadership";
const DEFAULT_DESCRIPTION =
  "Premium leadership books and courses for senior leaders. Home of Forged in Fire: The Course by Robbie South.";

function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({ title, description, noindex = false, canonicalPath }) {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Books & Courses for Leaders`;
    document.title = fullTitle;

    const desc = description || DEFAULT_DESCRIPTION;
    upsertMeta("description", desc);

    // OpenGraph + Twitter for social/AI previews
    upsertMeta("og:title", fullTitle, "property");
    upsertMeta("og:description", desc, "property");
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:site_name", SITE_NAME, "property");
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", desc);

    upsertMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    if (canonicalPath !== undefined) {
      const origin = window.location.origin;
      upsertLink("canonical", `${origin}${canonicalPath}`);
    } else {
      upsertLink("canonical", window.location.href.split("?")[0].split("#")[0]);
    }
  }, [title, description, noindex, canonicalPath]);

  return null;
}
